import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { identifyUser, resetAnalytics } from '../lib/analytics'

interface AuthState {
  token: string | null
  userId: string | null
  isLoading: boolean
  isAuthenticated: boolean
}

interface AuthContextType extends AuthState {
  login: () => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

const TOKEN_KEY = 'reframer_token'
const USER_ID_KEY = 'reframer_user_id'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: null,
    userId: null,
    isLoading: true,
    isAuthenticated: false,
  })

  // Load token from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    const userId = localStorage.getItem(USER_ID_KEY)

    if (token && userId) {
      setState({
        token,
        userId,
        isLoading: false,
        isAuthenticated: true,
      })
    } else {
      setState(s => ({ ...s, isLoading: false }))
    }
  }, [])

  const login = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/anon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        throw new Error('Failed to authenticate')
      }

      const data = await response.json()

      localStorage.setItem(TOKEN_KEY, data.token)
      localStorage.setItem(USER_ID_KEY, data.userId)

      // Identify user for analytics
      identifyUser(data.userId)

      setState({
        token: data.token,
        userId: data.userId,
        isLoading: false,
        isAuthenticated: true,
      })
    } catch (error) {
      console.error('Auth error:', error)
      throw error
    }
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_ID_KEY)

    // Reset analytics identity
    resetAnalytics()

    setState({
      token: null,
      userId: null,
      isLoading: false,
      isAuthenticated: false,
    })
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
